import React from 'react';
import { iconDefinitions, SvgElement } from './iconDefinitions';

export type IconName =
  | 'search'
  | 'star'
  | 'cross'
  | 'close'
  | 'user'
  | 'logout'
  | 'support'
  | 'hamburger'
  | 'facebook'
  | 'linkedin'
  | 'x'
  | 'basket'
  | 'lock-check'
  | 'logout'
  | 'mastercard'
  | 'plus'
  | 'minus'
  | 'cpanel'
  | 'cpanel-suspended'
  | 'wordpress'
  | 'wordpress-suspended'
  | 'question-red'
  | 'question-white'
  | 'pencil'
  | 'cog'
  | 'error-circle'
  | 'success-circle'
  | 'check-circle-grey'
  | 'check-circle-green'
  | 'clock'
  | 'arrow-right-green'
  | 'search'
  | 'support'
  | 'user'
  | 'visa'
  | 'delete-circle'
  | 'envelope'
  | 'globe'
  | 'database'
  | 'crown'
  | 'briefcase'
  | 'eye-open'
  | 'eye-closed'
  | 'plus'
  | 'minus'
  | 'cpanel'
  | 'cpanel-suspended'
  | 'wordpress'
  | 'wordpress-suspended'
  | 'question-red'
  | 'question-white'
  | 'pencil'
  | 'cog'
  | 'error-circle'
  | 'success-circle'
  | 'check-circle-grey'
  | 'arrow-right-green'
  | 'linux'
  | 'windows'
  | 'premium-hosting';

export interface IconProps {
  /**
   * Icon name
   */
  name: IconName;
  /**
   * Icon size in pixels
   */
  size?: number;
  /**
   * Icon color (uses currentColor by default, ignored for multiColor icons)
   */
  color?: string;
  /**
   * Custom CSS classes
   */
  className?: string;
  /**
   * Inline styles
   */
  style?: React.CSSProperties;
  /**
   * ARIA label for accessibility
   */
  'aria-label'?: string;
  /**
   * Hide from assistive tech. Set when the icon is purely decorative and sits
   * beside text that already conveys its meaning (e.g. a search glyph next to a
   * "Search" label), so it isn't announced as part of the parent's name.
   */
  'aria-hidden'?: boolean;
}

const renderElement = (el: SvgElement, index: number) => {
  // Convert attrs to React-friendly props (stroke-width → strokeWidth)
  const props: Record<string, string> = {};
  for (const [key, value] of Object.entries(el.attrs)) {
    if (key === 'stroke-width') {
      props.strokeWidth = value;
    } else {
      props[key] = value;
    }
  }
  return React.createElement(el.tag, { key: index, ...props });
};

export const Icon: React.FC<IconProps> = ({
  name,
  size = 20,
  color = 'currentColor',
  className = '',
  style,
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden,
}) => {
  const definition = iconDefinitions[name];

  if (!definition) {
    console.error(`Icon "${name}" not found in iconDefinitions`);
    return null;
  }

  const iconStyle: React.CSSProperties = {
    width: `${size}px`,
    height: `${size}px`,
    ...style,
  };

  const isFillIcon = definition.fill;
  const isMultiColor = definition.multiColor;
  const viewBox = definition.viewBox || '0 0 24 24';

  // Multi-element icons (elements array)
  if (definition.elements) {
    return (
      <svg
        className={`hi-ui-icon hi-ui-icon-${name} ${className}`.trim()}
        style={iconStyle}
        fill={isMultiColor ? 'none' : isFillIcon ? color : 'none'}
        viewBox={viewBox}
        xmlns="http://www.w3.org/2000/svg"
        aria-label={ariaHidden ? undefined : ariaLabel || name}
        aria-hidden={ariaHidden}
        role="img"
      >
        {definition.elements.map(renderElement)}
      </svg>
    );
  }

  // Single-path icons (backward compatible)
  return (
    <svg
      className={`hi-ui-icon hi-ui-icon-${name} ${className}`.trim()}
      style={iconStyle}
      fill={isFillIcon ? color : 'none'}
      stroke={isFillIcon ? 'none' : color}
      strokeWidth={definition.strokeWidth}
      viewBox={viewBox}
      xmlns="http://www.w3.org/2000/svg"
      aria-label={ariaHidden ? undefined : ariaLabel || name}
      aria-hidden={ariaHidden}
      role="img"
    >
      <path
        d={definition.path}
        fillRule={definition.fillRule}
        clipRule={definition.fillRule}
        strokeLinecap={isFillIcon ? undefined : 'round'}
        strokeLinejoin={isFillIcon ? undefined : 'round'}
      />
    </svg>
  );
};
